import type { UseQueryResult } from '@tanstack/react-query';
import type { AxiosError } from 'axios';
import { useGetData } from '../services/apiClient';
import { OSAT_API_ROUTES } from '../constants/osatRoutes';
import { retryUnless401 } from '../utils/retryPolicy';

/** Shape of `GET /v1.0/osat/eligibility`. */
export interface OsatEligibility {
  /** Whether the current user is eligible to be shown the OSAT survey now. */
  eligible: boolean;
}

/**
 * Fetch whether the current user is eligible for the OSAT survey.
 *
 * `GET /v1.0/osat/eligibility` (Foundation API). Same-day suppression is
 * server-side (`lastDismissedDate`), so the client only reads `eligible`.
 *
 * Gated by `enabled`: the caller passes `isFullyAuthenticated`, so the request
 * fires only once the user is fully authenticated — never during SSO / auth
 * initialization. Non-blocking: nothing renders off the loading/error states.
 *
 * @param enabled - gate; pass `isFullyAuthenticated`
 */
export const useOsatEligibility = (
  enabled: boolean,
): UseQueryResult<OsatEligibility, AxiosError> => {
  const baseUrl = import.meta.env.VITE_FOUNDATION_API_BASE_URL as string;

  return useGetData<OsatEligibility>(OSAT_API_ROUTES.ELIGIBILITY(baseUrl), {
    enabled,
    staleTime: 1000 * 60 * 5, // 5 minutes
    gcTime: 1000 * 60 * 10, // 10 minutes
    retry: retryUnless401(),
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
  });
};
